/**
 * Copyright © Binary Anvil, Inc. All rights reserved.
 * See https://binaryanvil.com/software/license for license details.
 */
import React from "react";
import classes from "@/app/components/algolia/Pagination/paginationStyles.module.scss";
import {usePagination} from "react-instantsearch";
import ArrowRight from "@/app/components/assets/common/ArrowRight";
import {useTranslation} from "next-i18next";

const Pagination = (props:{}) => {
    const {t} = useTranslation();
    const {
        pages,
        currentRefinement,
        nbPages,
        isFirstPage,
        isLastPage,
        refine,
        createURL,
    } = usePagination({
        padding: 2
    });
    const firstPageIndex = 0;
    const previousPageIndex = currentRefinement - 1;
    const nextPageIndex = currentRefinement + 1;
    const lastPageIndex = nbPages - 1;

    const refineWithScroll = (page:number) => {
        refine(page);
        const banner = document.getElementById('category-banner-wrapper');
        banner?.scrollIntoView({
            behavior: 'smooth'
        });
    }

    const pagesTransformed = Array.from(new Set([
        firstPageIndex,
        ...pages,
        lastPageIndex
    ]));

    if(pagesTransformed.length > 6) {
        pagesTransformed.splice(5,1);
        pagesTransformed.splice(1,1);
    }

    return <ul className={classes.pagination}>
        {!isFirstPage && <PaginationItem
            isDisabled={isFirstPage}
            href={createURL(previousPageIndex)}
            onClick={() => refineWithScroll(previousPageIndex)}
            attributes={{
                className: classes.prev
            }}
        ><span aria-label={t('Previous')}><ArrowRight/></span></PaginationItem>}
        {pagesTransformed.map((page, index) => {
            const label = page + 1;
            let content = <PaginationItem
                key={`${page}-${currentRefinement}`}
                isDisabled={page === currentRefinement}
                href={createURL(page)}
                attributes={{
                    className: page === currentRefinement ? classes.active : '',
                }}
                onClick={() => refineWithScroll(page)}
            >
                {label}
            </PaginationItem>;
            if(index > 0 && (page - pagesTransformed[index - 1]) > 1) {
                content = <React.Fragment key={index}>
                    <li className={`${classes.item} ${classes.dots}`}>...</li>
                    {content}
                </React.Fragment>;
            }
            return content;
        })}
        {!isLastPage && <PaginationItem
            isDisabled={isLastPage}
            href={createURL(nextPageIndex)}
            onClick={() => refineWithScroll(nextPageIndex)}
            attributes={{
                className: classes.next
            }}
        ><span aria-label={t('Next')}><ArrowRight/></span></PaginationItem>}
    </ul>
}

const PaginationItem = (props: {
    isDisabled: boolean,
    href: string,
    onClick: any,
    attributes?: any,
    children: any,
}) => {
    if (props.isDisabled) {
        return (
            <li className={classes.item}>
                <span {...props.attributes}>{props.children}</span>
            </li>
        );
    }

    return (
        <li className={classes.item}>
            <a
                href={props.href}
                onClick={(event: any) => {
                    if (isModifierClick(event)) {
                        return;
                    }

                    event.preventDefault();

                    props.onClick(event);
                }}
                {...props.attributes}
            >
                {props.children}
            </a>
        </li>
    );
}

function isModifierClick(event: any) {
    const isMiddleClick = event.button === 1;

    return Boolean(
        isMiddleClick ||
        event.altKey ||
        event.ctrlKey ||
        event.metaKey ||
        event.shiftKey
    );
}

export default Pagination;